import { Link, useLocation } from "react-router-dom";
import { Linkedin, Twitter, Facebook, Instagram, MapPin, Phone, Mail } from "lucide-react";
import { Logo } from "./Logo";
import { ADDRESS, EMAIL, NAV_LINKS, NAV_MEGA, NAV_MORE, PHONE } from "@/lib/data";

// The Other Services hub has no sub-categories in the navbar, so the footer
// lists these headline services under it instead of all eleven.
const FEATURED_SERVICES = [
  "/other-services/audit-support-services",
  "/other-services/startup-india-dpiit-recognition",
  "/other-services/msme-advisory",
  "/other-services/funding-subsidy-advisory",
];

// Sitemap columns are derived from the navbar so the footer can never drift
// out of sync with it — one column per top-level nav item with a `mega`
// config, listing its sub-categories (the mega-menu tabs). Individual
// services are left to the mega menu and the hub pages.
const SITEMAP = NAV_LINKS.filter((l) => l.mega).map((l) => ({
  title: l.label,
  href: l.href,
  // The tab that just repeats the column title is dropped. A tab with no
  // sub-hub of its own (Registration's groups) has nowhere distinct to link,
  // so it renders as plain text rather than a duplicate of the column link.
  links: (() => {
    const tabs = NAV_MEGA[l.label].tabs;
    const groups = tabs.filter((t) => t.label !== l.label);
    // A single tab that just repeats the column title (Other Services) leaves
    // no sub-categories to show, so list its headline services instead —
    // FEATURED_SERVICES keeps the column to the same length as the others.
    if (!groups.length)
      return tabs
        .flatMap((t) => t.items)
        .filter((i) => FEATURED_SERVICES.includes(i.href));
    return groups.map((t) => ({ label: t.label, href: t.href !== l.href ? t.href : null }));
  })(),
}));

const COMPANY = [...NAV_MORE, { label: "Contact", href: "/contact" }];

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn" },
  { icon: Twitter, label: "Twitter" },
  { icon: Facebook, label: "Facebook" },
  { icon: Instagram, label: "Instagram" },
];

const LEGAL = ["Privacy Policy", "Terms", "Data Security", "Data Retention", "Refund Policy", "Cookie Policy", "Grievance Redressal"];

export const Footer = () => {
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  const FooterLink = ({ href, className, children }) => {
    if (href === "#") {
      return <a href={href} className={className}>{children}</a>;
    }
    if (href.startsWith("#")) {
      return isHome ? (
        <a href={href} className={className}>{children}</a>
      ) : (
        <Link to={`/${href}`} className={className}>{children}</Link>
      );
    }
    return <Link to={href} className={className}>{children}</Link>;
  };

  return (
    <footer className="relative bg-navy-950 text-white" data-testid="footer">
      {/* Full sitemap — mirrors the navbar's mega menus */}
      <div>
        <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {SITEMAP.map((col) => (
              <div key={col.title} data-testid={`footer-col-${col.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <FooterLink href={col.href} className="text-sm font-bold uppercase tracking-wider text-white transition-colors hover:text-gold-400">
                  {col.title}
                </FooterLink>
                <ul className="mt-5 space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.label} className="text-sm text-white/60">
                      {l.href ? (
                        <FooterLink href={l.href} className="transition-colors hover:text-gold-400">
                          {l.label}
                        </FooterLink>
                      ) : (
                        l.label
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl border-t border-white/10 px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo variant="light" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
              Your one-stop business compliance partner — from registration to growth.
              Professional-led advisory, delivered digitally across India.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map(({ icon: SIcon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 transition-[color,background-color,border-color] duration-300 hover:border-gold-400 hover:bg-gold-500 hover:text-navy-900"
                >
                  <SIcon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-sm font-bold uppercase tracking-wider text-white">Company</h4>
            <ul className="mt-5 grid grid-cols-2 gap-3">
              {COMPANY.map((l) => (
                <li key={l.label}>
                  <FooterLink href={l.href} className="text-sm text-white/60 transition-colors hover:text-gold-400">
                    {l.label}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-sm font-bold uppercase tracking-wider text-white">Get in Touch</h4>
            <ul className="mt-5 space-y-4 text-sm text-white/60">
              <li>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-3 transition-colors hover:text-gold-400"
                  data-testid="footer-address-link"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-400 transition-transform group-hover:scale-110" />
                  <span>{ADDRESS}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${PHONE.replace(/\s+/g, "")}`}
                  className="group flex items-center gap-3 transition-colors hover:text-gold-400"
                  data-testid="footer-phone-link"
                >
                  <Phone className="h-4 w-4 shrink-0 text-gold-400 transition-transform group-hover:scale-110" />
                  <span>{PHONE}</span>
                </a>
              </li>
              <li>
                <Link
                  to="/contact#contact-form"
                  onClick={(e) => {
                    if (pathname === "/contact") {
                      const el = document.getElementById("contact-form") || document.getElementById("enquiry-form");
                      if (el) {
                        e.preventDefault();
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }
                  }}
                  className="group flex items-center gap-3 transition-colors hover:text-gold-400"
                  data-testid="footer-email-link"
                >
                  <Mail className="h-4 w-4 shrink-0 text-gold-400 transition-transform group-hover:scale-110" />
                  <span>{EMAIL}</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-6 text-xs text-white/50 sm:flex-row lg:px-8">
          <p data-testid="footer-copyright">© 2026 Biz360 Compliance. All rights reserved.</p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {LEGAL.map((l) => (
              <li key={l}>
                {l === "Privacy Policy" ? (
                  <Link to="/privacy-policy" className="transition-colors hover:text-gold-400">{l}</Link>
                ) : l === "Terms" ? (
                  <Link to="/terms" className="transition-colors hover:text-gold-400">{l}</Link>
                ) : l === "Data Security" ? (
                  <Link to="/data-security-policy" className="transition-colors hover:text-gold-400">{l}</Link>
                ) : l === "Data Retention" ? (
                  <Link to="/data-retention-policy" className="transition-colors hover:text-gold-400">{l}</Link>
                ) : l === "Refund Policy" ? (
                  <Link to="/refund-policy" className="transition-colors hover:text-gold-400">{l}</Link>
                ) : l === "Cookie Policy" ? (
                  <Link to="/cookie-policy" className="transition-colors hover:text-gold-400">{l}</Link>
                ) : l === "Grievance Redressal" ? (
                  <Link to="/grievance-redressal-policy" className="transition-colors hover:text-gold-400">{l}</Link>
                ) : (
                  <a href="#" className="transition-colors hover:text-gold-400">{l}</a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};
